import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PulseLoader from "@/components/PulseLoader";
import { set } from "date-fns";
import { authService } from "../../../utils/authService";
import { fetchBatchesByCourse } from "../../../utils/batchUtils";

interface Course {
  course_id: string;
  course_name: string;
}

interface Batch {
  batch_id: string;
  batch_name: string;
  del_row?: boolean;
}

interface FormData {
  hoursPerDay: string;
  saturdayStudyDay: string;
  startDate: string;
  sundayHoliday: string;
  batch_id: string; // Add batch_id to formData
}

const AssignBatch: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedCourse, setSelectedCourse] = useState<string>("");
  const [selectedBatch, setSelectedBatch] = useState<string>(""); // To store selected batch
  const [loading, setLoading] = useState<boolean>(false);
  const [questionsDisplay, setQuestionsDisplay] = useState<boolean>(false);
  const [modalNewMessage, setModalNewMessage] = useState<string>("");
  const [showUpdateModal, setShowUpdateModal] = useState<boolean>(false);
  const [weekendSettingsExist, setWeekendSettingsExist] = useState<boolean>(false);
  const [checkingBatchData, setCheckingBatchData] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    hoursPerDay: "",
    saturdayStudyDay: "No",
    startDate: "",
    sundayHoliday: "No",
    batch_id: "", // Initialize batch_id
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await authService.get('admin/batch/get_all_courses_for_batch/');
      setCourses(response.data.courses);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error('Error fetching courses:', error);
    }
  };

  useEffect(() => {
    if (selectedCourse !== "") {
      fetchBatches();
    }
  }, [selectedCourse]);

  const fetchBatches = async () => {
    try {
      setLoading(true);
      const data = await fetchBatchesByCourse(selectedCourse);
      // Filter out batches with del_row = true
      const activeBatches = data.filter((batch: Batch) => !batch.del_row);
      setBatches(activeBatches);
      setLoading(false);
      setCheckingBatchData(false);
    } catch (error) {
      setLoading(false);
      setCheckingBatchData(false);
    }
  };

  const handleCourseChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = event.target.value;
    setSelectedCourse(selectedValue);
    setSelectedBatch(""); // Reset batch dropdown for new course
    setBatches([]); // Clear batches when course changes
  };

  const handleBatchChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const batchId = event.target.value;
    setSelectedBatch(batchId);
    setFormData((prevData) => ({ ...prevData, batch_id: batchId }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };


  const handleGoButton = async () => {
    if (!questionsDisplay) {
      // Check if batch data exists before showing the form
      if (selectedBatch) {
        setCheckingBatchData(true);
        try {
          const response = await authService.get(`admin/batch/check_if_batch_data_exists/${selectedBatch}/`);
          const exists = response.data?.exist || false;
          setWeekendSettingsExist(exists);
          
          if (exists) {
            // Show message that daywise plan already exists
            setModalNewMessage("Daywise plan already exists for the batch. Edit Daywise plan");
            setShowUpdateModal(true);
            setCheckingBatchData(false);
            return;
          }
          
          // If no existing data, show the form
          setQuestionsDisplay(true);
        } catch (error) {
          console.error('Error checking batch data:', error);
          setModalNewMessage("Failed to check batch data. Please try again.");
          setShowUpdateModal(true);
        } finally {
          setCheckingBatchData(false);
        }
      } else {
        setQuestionsDisplay(true);
      }
    } else {
      try {
        const payload = {
          ...formData,
          course_id: selectedCourse,
          weekend: {
            saturday: formData.saturdayStudyDay === "Yes",
            sunday: formData.sundayHoliday === "Yes",
          },
        };
        const selectedDate = new Date(formData.startDate);
        selectedDate.setDate(selectedDate.getDate());
        const adjustedDate = selectedDate.toISOString().split("T")[0];
        const response = await authService.post(
          "admin/update_weekend_holidays/",
          {
            saturday: formData.saturdayStudyDay === "Yes",
            sunday: formData.sundayHoliday === "Yes",
            batch_id: selectedBatch,
            course_id: selectedCourse,
            hours : Number(formData.hoursPerDay)
          }
        );
  
        if (response.data?.status === "ok") {
          navigate("/admin/day-wise-group", {
            state: {
              data: {
                ...payload,
                startDate: adjustedDate,
              },
            },
          });
        } else {
          // alert("Database error: " + (response.data?.message || "Unknown error"));
          setModalNewMessage("Database error: " + (response.data?.message || "Unknown error"));
          setShowUpdateModal(true);
        }
      } catch (error: any) {
        console.error("Error saving batch", error);
        // alert("Failed to submit. Please check your network or server.");
        setModalNewMessage("Failed to submit. Please check your network or server.");
        setShowUpdateModal(true);
      }
    }
  };
  
  return (
    <div
      className="border pt-3 px-2 rounded-2 bg-white my-2 me-2"
      style={{ height: `calc(100vh - 90px)`, overflowY: "auto" }}
    >
      <div>
        <div className="container-fluid mt-2">
          <h4 className="text-center mb-2">Assign Batches</h4>
          <div className="d-flex justify-content-center align-items-center">
            <div className="d-flex justify-content-center mt-3">
              <label>Select Course:</label>
              <select
                style={{ width: "250px" }}
                className="form-select ms-2 form-select-sm"
                onChange={handleCourseChange}
                disabled={questionsDisplay}
                value={selectedCourse}
              >
                <option value="">Select Course</option>
                {courses.map((course) => (
                  <option key={course.course_id} value={course.course_id}>
                    {course.course_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              {selectedCourse && batches.length > 0 ? (
                <div className="d-flex justify-content-center mt-3 ms-2">
                  <label>Select Batch:</label>
                  <select
                    style={{ width: "250px" }} // Same width as Course dropdown
                    className="form-select ms-2 form-select-sm"
                    onChange={handleBatchChange}
                    value={selectedBatch}
                    disabled={questionsDisplay}
                  >
                    <option value="">Select Batch</option>
                    {batches.map((batch: Batch) => (
                      <option key={batch.batch_id} value={batch.batch_id}>
                        {batch.batch_name}
                      </option>
                    ))}
                  </select>
                  {questionsDisplay ? (
                    <button
                      className="rounded-2 ms-3"
                      onClick={() => setQuestionsDisplay(false)}
                    >
                      Change Course
                    </button>
                  ) : (
                    <button
                      onClick={handleGoButton}
                      className="rounded-2 ms-3"
                      disabled={!selectedCourse || !selectedBatch || checkingBatchData}
                    >
                      {checkingBatchData ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Checking...
                        </>
                      ) : (
                        "Go"
                      )}
                    </button>
                  )}
                </div>
              ) : (
                selectedCourse && !loading && batches.length === 0 && (
                  <div className="d-flex justify-content-center mt-3">
                    <p className="text-danger">Please create a batch first.</p>
                  </div>
                )
              )}
            </div>
          </div>

          {questionsDisplay && (
            <div className="card p-3 shadow-sm mt-3 container-md">
              <div className="mb-3">
                <label className="form-label fw-bold">
                  No. of Hours per Day:
                </label>
                <input
                  type="number"
                  className="form-control"
                  name="hoursPerDay"
                  value={formData.hoursPerDay}
                  onChange={handleInputChange}
                  min="1"
                  max="16"
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">
                  Do you want Saturday as study day?
                </label>
                <div className="form-check">
  <input
    className="form-check-input"
    type="radio"
    name="saturdayStudyDay"
    value="Yes"
    checked={formData.saturdayStudyDay === "Yes"}
    onChange={handleInputChange}
  />
  <label className="form-check-label">Yes</label>
</div>
<div className="form-check">
  <input
    className="form-check-input"
    type="radio"
    name="saturdayStudyDay"
    value="No"
    checked={formData.saturdayStudyDay === "No"}
    onChange={handleInputChange}
  />
  <label className="form-check-label">No</label>
</div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">
                  Do you want Sunday as a holiday?
                </label>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sundayHoliday"
                    value="Yes"
                    checked={formData.sundayHoliday == "Yes"}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label">Yes</label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="sundayHoliday"
                    value="No"
                    checked={formData.sundayHoliday == "No"}
                    onChange={handleInputChange}
                  />
                  <label className="form-check-label">No</label>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Start Date:</label>
                <input
                  type="date"
                  className="form-control"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleInputChange}
                />
              </div>

              <button
                onClick={handleGoButton}
                className="btn btn-primary w-100 mt-2"
                disabled={
                  !formData.hoursPerDay ||
                  !formData.saturdayStudyDay ||
                  !formData.sundayHoliday ||
                  !formData.startDate ||
                  !formData.batch_id
                }
              >
                Submit
              </button>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            zIndex: 9999,
          }}
        >
          <PulseLoader size={10} />
        </div>
      )}

      {/* Modal for displaying messages */}
      {showUpdateModal && (
        <div
          className="modal fade show"
          style={{ display: "block" }}
          tabIndex={-1}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Message</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowUpdateModal(false);
                    navigate("/admin/day-wise-card");
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>{modalNewMessage}</p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowUpdateModal(false);
                    navigate("/admin/day-wise-card");
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal backdrop */}
      {showUpdateModal && (
        <div
          className="modal-backdrop fade show"
          style={{ zIndex: 1040 }}
        ></div>
      )}
    </div>
  );
};

export default AssignBatch;
